import { Box } from '@mui/material'
import { GridActionsCellItem, GridColumns, GridNativeColTypes } from '@mui/x-data-grid-pro'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdEdit } from 'react-icons/md'
import { taskEndpoints } from 'src/api/tasks'
import ToggleGroupButtons from 'src/components/common/ToggleGroupButtons'
import GenericTable from 'src/components/table/GenericTable'
import { Flex } from 'src/styles/flexComponent'
import { getFormattedDate } from 'src/utils'
import { EdgeCase, getUserDetail, UserDetail } from 'src/utils/getUserDetails'
import { useDialogCtx } from 'src/hooks/context-hooks'
import TaskDeleteButton from './TaskDeleteButton'
import TaskEditForm from './TaskEditForm'
import { taskIntervalList } from './task-constants'

interface RecurringTasksTabProps {
  customerId: EntityId
  localityId?: EntityId
}

const RecurringTasksTab: React.FC<RecurringTasksTabProps> = ({ customerId, localityId }) => {
  const { t } = useTranslation(['task', 'common'])
  const { showDialog } = useDialogCtx()
  const [isOutdated, setIsOutdated] = useState(false)

  const editSeries = useCallback(
    row => {
      showDialog(TaskEditForm, {
        componentProps: {
          initialValues: row,
          customerId,
          tasksId: row?.id,
          localityId: row?.localityId,
        },
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
        title: t('edit-task'),
      })
    },
    [customerId, showDialog, t],
  )

  const columns: GridColumns = useMemo(
    () => [
      { field: 'id', headerName: t('id', { ns: 'common' }), minWidth: 80, maxWidth: 100, flex: 0 },
      { field: 'title', headerName: t('title', { ns: 'common' }), flex: 1, cellClassName: 'font-weight-semi-bold' },
      {
        field: 'locality.name',
        headerName: t('locality', { ns: 'common' }),
        flex: 1,
        renderCell: ({ row }) => row.locality?.name ?? '-',
      },
      {
        field: 'interval',
        headerName: t('interval', { ns: 'task' }),
        flex: 1,
        renderCell: ({ value }) => taskIntervalList.find(i => i.value === value)?.option ?? '-',
      },
      {
        field: 'duration',
        headerName: t('duration', { ns: 'task' }),
        flex: 0.7,
        renderCell: ({ value }) => value ?? '-',
      },
      {
        field: 'startDate',
        headerName: t('startDate', { ns: 'common' }),
        flex: 1,
        renderCell: ({ row }) => getFormattedDate(row.startDate),
      },
      {
        field: 'endDate',
        headerName: t('endDate', { ns: 'common' }),
        flex: 1,
        renderCell: ({ row }) => getFormattedDate(row.endDate),
      },
      {
        field: 'responsibleUser',
        headerName: t('responsibleUser', { ns: 'locality' }),
        flex: 1,
        renderCell: ({ value }) =>
          value ? getUserDetail(value, UserDetail.FullName, EdgeCase.ReturnEmailForNewUser) : '-',
      },
      {
        field: 'actions',
        type: 'actions' as GridNativeColTypes,
        width: 60,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key={`edit-${row.id}`}
            label={t('edit', { ns: 'common' })}
            icon={<MdEdit size={24} />}
            onClick={() => editSeries(row)}
            color="primary"
            showInMenu
          />,
          <GridActionsCellItem
            key={`delete-${row.id}`}
            label={''}
            icon={<TaskDeleteButton task={row} customerId={customerId} />}
            color="primary"
            showInMenu
          />,
        ],
      },
    ],
    [customerId, editSeries, t],
  )

  return (
    <>
      <Flex.Row justifyContent={'space-between'} alignItems={'center'} flexWrap={'wrap'} gap={1} padding={'8px'}>
        <ToggleGroupButtons
          actions={[
            { title: t('active', { ns: 'task' }), value: 'active' },
            { title: t('outdated', { ns: 'task' }), value: 'outdated' },
          ]}
          defaultValue={isOutdated ? 'outdated' : 'active'}
          value={isOutdated ? 'outdated' : 'active'}
          setViewMode={v => setIsOutdated(v === 'outdated')}
          buttonWidth="auto"
        />
      </Flex.Row>
      <Box padding={'8px'}>
        <GenericTable
          queryUrl={taskEndpoints.filterTask(customerId)}
          queryParams={{
            isRecurring: true,
            isOutdated,
            ...(localityId ? { localityIds: String(localityId) } : {}),
          }}
          columns={columns}
          onRowClick={({ row }) => editSeries(row)}
          enableServerPagination
          id={'recurring-tasks'}
          autoHeight={false}
          height={window.innerHeight - 380 + 'px'}
        />
      </Box>
    </>
  )
}

export default RecurringTasksTab
